import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Video, Plus, Edit, Trash2, ArrowLeft, Film, Clock, 
  Upload, X, GraduationCap, Clapperboard, FileVideo, Save, Library
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { adminFetch, isAdminAuthenticated } from '@/utils/adminAuth';
import { toast } from 'sonner';
import { MediaLibraryPicker } from '@/components/admin/MediaLibraryPicker';

const CATEGORIES = [
  { id: 'instructional', label: 'Instructional', icon: GraduationCap },
  { id: 'performance', label: 'Performance', icon: Clapperboard },
  { id: 'documentary', label: 'Documentary', icon: FileVideo },
];

export const AdminVideos = () => {
  const [courses, setCourses] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCourseModal, setShowCourseModal] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [editingVideo, setEditingVideo] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [mediaPickerTarget, setMediaPickerTarget] = useState(null); // 'course_thumbnail', 'course_trailer', 'video_url', 'video_thumbnail', 'video_trailer'
  const navigate = useNavigate();

  const openMediaPicker = (target) => {
    setMediaPickerTarget(target);
    setShowMediaPicker(true);
  };

  const handleMediaSelect = (selectedItems) => {
    const list = Array.isArray(selectedItems) ? selectedItems : (selectedItems ? [selectedItems] : []);
    if (list.length === 0) return;
    const item = list[0];
    const fullUrl = item.url.startsWith('http') ? item.url : `${process.env.REACT_APP_BACKEND_URL}${item.url}`;
    
    if (mediaPickerTarget.startsWith('course_')) {
      const field = mediaPickerTarget.replace('course_', '') + '_url';
      setCourseForm(prev => ({ ...prev, [field]: fullUrl }));
    } else if (mediaPickerTarget.startsWith('video_')) {
      const field = mediaPickerTarget.replace('video_', '');
      const fieldName = field === 'url' ? 'video_url' : field + '_url';
      setVideoForm(prev => ({ ...prev, [fieldName]: fullUrl }));
    }
    
    toast.success('Media selected!');
    setShowMediaPicker(false);
    setMediaPickerTarget(null);
  };

  const [courseForm, setCourseForm] = useState({
    title: '',
    description: '',
    instructor: 'Mestre Curtis Pierre',
    category: 'instructional',
    price: '',
    thumbnail_url: '',
    trailer_url: '',
  });

  const [videoForm, setVideoForm] = useState({
    title: '',
    description: '',
    duration: '',
    video_url: '',
    trailer_url: '',
    thumbnail_url: '',
    course_id: '',
    order: 0,
  });

  useEffect(() => {
    if (!isAdminAuthenticated()) {
      navigate('/admin');
      return;
    }
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [coursesRes, videosRes] = await Promise.all([
        adminFetch('/admin/video-courses'),
        adminFetch('/admin/videos')
      ]);
      setCourses(await coursesRes.json());
      setVideos(await videosRes.json());
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadVideo = async (e, field) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const endpoint = field.includes('thumbnail') ? '/admin/upload/cover' : '/admin/upload/video';
      const response = await adminFetch(endpoint, {
        method: 'POST',
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        if (showVideoModal) {
          setVideoForm({ ...videoForm, [field]: data.url });
        } else {
          setCourseForm({ ...courseForm, [field]: data.url });
        }
        toast.success('File uploaded!');
      }
    } catch (error) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleSaveCourse = async () => {
    if (!courseForm.title || !courseForm.price) {
      toast.error('Title and price are required');
      return;
    }

    try {
      const endpoint = editingCourse 
        ? `/admin/video-courses/${editingCourse.id}`
        : '/admin/video-courses';
      const method = editingCourse ? 'PUT' : 'POST';

      const response = await adminFetch(endpoint, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...courseForm,
          price: parseFloat(courseForm.price),
        })
      });

      if (response.ok) {
        toast.success(editingCourse ? 'Course updated!' : 'Course created!');
        setShowCourseModal(false);
        resetCourseForm();
        loadData();
      }
    } catch (error) {
      toast.error('Failed to save course');
    }
  };

  const handleSaveVideo = async () => {
    if (!videoForm.title || !videoForm.video_url) {
      toast.error('Title and video URL are required');
      return;
    }

    try {
      const endpoint = editingVideo 
        ? `/admin/videos/${editingVideo.id}`
        : '/admin/videos';
      const method = editingVideo ? 'PUT' : 'POST';

      const response = await adminFetch(endpoint, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...videoForm,
          duration: parseInt(videoForm.duration) || 0,
          order: parseInt(videoForm.order) || 0,
        })
      });

      if (response.ok) {
        toast.success(editingVideo ? 'Video updated!' : 'Video created!');
        setShowVideoModal(false);
        resetVideoForm();
        loadData();
      }
    } catch (error) {
      toast.error('Failed to save video');
    }
  };

  const handleDeleteCourse = async (courseId) => {
    if (!confirm('Delete this course?')) return;
    
    try {
      await adminFetch(`/admin/video-courses/${courseId}`, { method: 'DELETE' });
      toast.success('Course deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleDeleteVideo = async (videoId) => {
    if (!confirm('Delete this video?')) return;
    
    try {
      await adminFetch(`/admin/videos/${videoId}`, { method: 'DELETE' });
      toast.success('Video deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const openEditCourse = (course) => {
    setCourseForm({
      title: course.title || '',
      description: course.description || '',
      instructor: course.instructor || 'Mestre Curtis Pierre',
      category: course.category || 'instructional',
      price: course.price?.toString() || '',
      thumbnail_url: course.thumbnail_url || '',
      trailer_url: course.trailer_url || '',
    });
    setEditingCourse(course);
    setShowCourseModal(true);
  };

  const openEditVideo = (video) => {
    setVideoForm({
      title: video.title || '',
      description: video.description || '',
      duration: video.duration?.toString() || '',
      video_url: video.video_url || '',
      trailer_url: video.trailer_url || '',
      thumbnail_url: video.thumbnail_url || '',
      course_id: video.course_id || '',
      order: video.order || 0,
    });
    setEditingVideo(video);
    setShowVideoModal(true);
  };

  const resetCourseForm = () => {
    setCourseForm({
      title: '',
      description: '',
      instructor: 'Mestre Curtis Pierre',
      category: 'instructional',
      price: '',
      thumbnail_url: '',
      trailer_url: '',
    });
    setEditingCourse(null);
  };

  const resetVideoForm = () => {
    setVideoForm({
      title: '',
      description: '',
      duration: '',
      video_url: '',
      trailer_url: '',
      thumbnail_url: '',
      course_id: '',
      order: 0,
    });
    setEditingVideo(null);
  };

  const formatDuration = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    return hrs > 0 ? `${hrs}h ${mins}m` : `${mins} min`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background noise-texture py-8 px-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="p-2 text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div>
              <h1 className="text-4xl font-bold font-display text-foreground">Video Management</h1>
              <p className="text-muted-foreground mt-1">
                {courses.length} courses • {videos.length} videos
              </p>
            </div>
          </div>
          
          <div className="flex gap-3">
            <button
              onClick={() => { resetVideoForm(); setShowVideoModal(true); }}
              className="flex items-center gap-2 px-5 py-3 bg-card border border-border hover:border-primary/50 rounded-full font-bold text-foreground transition-all"
            >
              <Plus className="w-5 h-5" />
              Add Video
            </button>
            <button
              onClick={() => { resetCourseForm(); setShowCourseModal(true); }}
              className="flex items-center gap-2 px-6 py-3 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full font-bold transition-all hover:scale-105"
            >
              <Plus className="w-5 h-5" />
              New Course
            </button>
          </div>
        </div>

        {/* Courses Grid */}
        <h2 className="text-2xl font-bold text-foreground mb-4 flex items-center gap-2">
          <Film className="w-6 h-6 text-primary" />
          Video Courses
        </h2>
        
        {courses.length === 0 ? (
          <div className="text-center py-16 bg-card border border-border/50 rounded-2xl mb-8">
            <Video className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
            <h3 className="text-xl font-bold text-foreground mb-2">No courses yet</h3>
            <p className="text-muted-foreground">Create your first video course to get started</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {courses.map((course) => (
              <motion.div
                key={course.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-card border border-border/50 rounded-2xl overflow-hidden hover:border-primary/30 transition-all"
              >
                <div className="aspect-video bg-stone-800 relative">
                  {course.thumbnail_url ? (
                    <img loading="lazy" decoding="async" src={course.thumbnail_url} alt={course.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Video className="w-12 h-12 text-muted-foreground" />
                    </div>
                  )}
                  <span className="absolute top-3 right-3 px-3 py-1 bg-black/60 rounded-full text-xs font-bold text-white">
                    {course.video_count || 0} videos
                  </span>
                </div>
                <div className="p-4">
                  <h3 className="font-bold text-foreground mb-1">{course.title}</h3>
                  <div className="flex items-center gap-3 text-sm text-muted-foreground mb-3">
                    <span className="capitalize">{course.category}</span>
                    <span>${course.price?.toFixed(2)}</span>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => openEditCourse(course)}
                      className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-background rounded-lg text-foreground hover:bg-primary/10 transition-colors"
                    >
                      <Edit className="w-4 h-4" /> Edit
                    </button>
                    <button
                      onClick={() => handleDeleteCourse(course.id)}
                      className="px-3 py-2 bg-red-500/10 rounded-lg text-red-500 hover:bg-red-500/20 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Videos List */}
        <h2 className="text-2xl font-bold text-foreground mb-4 flex items-center gap-2">
          <Video className="w-6 h-6 text-primary" />
          Individual Videos
        </h2>
        
        {videos.length === 0 ? (
          <div className="text-center py-12 bg-card border border-border/50 rounded-2xl">
            <p className="text-muted-foreground">No individual videos yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {videos.map((video) => (
              <div
                key={video.id}
                className="flex items-center gap-4 p-4 bg-card border border-border/50 rounded-xl hover:border-primary/30 transition-all"
              >
                <div className="w-24 h-16 bg-stone-800 rounded-lg overflow-hidden flex-shrink-0">
                  {video.thumbnail_url ? (
                    <img loading="lazy" decoding="async" src={video.thumbnail_url} alt={video.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Video className="w-6 h-6 text-muted-foreground" />
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <h4 className="font-bold text-foreground">{video.title}</h4>
                  <div className="flex items-center gap-3 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {formatDuration(video.duration || 0)}
                    </span>
                    {video.course_id && (
                      <span>
                        Course: {courses.find(c => c.id === video.course_id)?.title || 'Unknown'}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => openEditVideo(video)}
                    className="p-2 bg-background rounded-lg text-foreground hover:bg-primary/10 transition-colors"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteVideo(video.id)}
                    className="p-2 bg-red-500/10 rounded-lg text-red-500 hover:bg-red-500/20 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Course Modal */}
      <AnimatePresence>
        {showCourseModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 overflow-y-auto"
            onClick={() => setShowCourseModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="bg-card border border-border rounded-2xl max-w-2xl w-full my-8"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-4 border-b border-border flex items-center justify-between">
                <h3 className="text-xl font-bold text-foreground">
                  {editingCourse ? 'Edit Course' : 'New Course'}
                </h3>
                <button onClick={() => setShowCourseModal(false)} className="p-2 hover:bg-background rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
                <div>
                  <label className="block text-sm font-bold text-muted-foreground mb-1">Title *</label>
                  <input
                    type="text"
                    value={courseForm.title}
                    onChange={(e) => setCourseForm({ ...courseForm, title: e.target.value })}
                    className="w-full px-4 py-3 bg-background border border-border rounded-xl text-foreground"
                    placeholder="Course title"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-bold text-muted-foreground mb-1">Description</label>
                  <textarea
                    value={courseForm.description}
                    onChange={(e) => setCourseForm({ ...courseForm, description: e.target.value })}
                    className="w-full px-4 py-3 bg-background border border-border rounded-xl text-foreground"
                    rows={3}
                    placeholder="Course description"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-muted-foreground mb-1">Category</label>
                    <select
                      value={courseForm.category}
                      onChange={(e) => setCourseForm({ ...courseForm, category: e.target.value })}
                      className="w-full px-4 py-3 bg-background border border-border rounded-xl text-foreground"
                    >
                      {CATEGORIES.map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-muted-foreground mb-1">Price *</label>
                    <input
                      type="number"
                      step="0.01"
                      value={courseForm.price}
                      onChange={(e) => setCourseForm({ ...courseForm, price: e.target.value })}
                      className="w-full px-4 py-3 bg-background border border-border rounded-xl text-foreground"
                      placeholder="29.99"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold text-muted-foreground mb-1">Instructor</label>
                  <input
                    type="text"
                    value={courseForm.instructor}
                    onChange={(e) => setCourseForm({ ...courseForm, instructor: e.target.value })}
                    className="w-full px-4 py-3 bg-background border border-border rounded-xl text-foreground"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-muted-foreground mb-1">Thumbnail</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={courseForm.thumbnail_url}
                      onChange={(e) => setCourseForm({ ...courseForm, thumbnail_url: e.target.value })}
                      className="flex-1 px-4 py-3 bg-background border border-border rounded-xl text-foreground"
                      placeholder="URL or browse library"
                    />
                    <button 
                      type="button"
                      onClick={() => openMediaPicker('course_thumbnail')}
                      className="px-4 py-3 bg-amber-500/20 text-amber-500 rounded-xl hover:bg-amber-500/30 transition-colors flex items-center gap-2"
                    >
                      <Library className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold text-muted-foreground mb-1">Trailer URL (optional)</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={courseForm.trailer_url}
                      onChange={(e) => setCourseForm({ ...courseForm, trailer_url: e.target.value })}
                      className="flex-1 px-4 py-3 bg-background border border-border rounded-xl text-foreground"
                      placeholder="30-60 second preview video URL"
                    />
                    <button 
                      type="button"
                      onClick={() => openMediaPicker('course_trailer')}
                      className="px-4 py-3 bg-purple-500/20 text-purple-500 rounded-xl hover:bg-purple-500/30 transition-colors flex items-center gap-2"
                    >
                      <Library className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>

              <div className="p-4 border-t border-border flex gap-3 justify-end">
                <button
                  onClick={() => setShowCourseModal(false)}
                  className="px-6 py-3 bg-background border border-border rounded-xl font-medium text-foreground hover:bg-card transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveCourse}
                  disabled={uploading}
                  className="flex items-center gap-2 px-6 py-3 bg-primary hover:bg-primary/90 rounded-xl font-bold text-primary-foreground transition-colors disabled:opacity-50"
                >
                  <Save className="w-4 h-4" />
                  {uploading ? 'Uploading...' : (editingCourse ? 'Update' : 'Create')}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Video Modal */}
      <AnimatePresence>
        {showVideoModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 overflow-y-auto"
            onClick={() => setShowVideoModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="bg-card border border-border rounded-2xl max-w-2xl w-full my-8"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-4 border-b border-border flex items-center justify-between">
                <h3 className="text-xl font-bold text-foreground">
                  {editingVideo ? 'Edit Video' : 'Add Video'}
                </h3>
                <button onClick={() => setShowVideoModal(false)} className="p-2 hover:bg-background rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
                <div>
                  <label className="block text-sm font-bold text-muted-foreground mb-1">Title *</label>
                  <input
                    type="text"
                    value={videoForm.title}
                    onChange={(e) => setVideoForm({ ...videoForm, title: e.target.value })}
                    className="w-full px-4 py-3 bg-background border border-border rounded-xl text-foreground"
                    placeholder="Video title"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-muted-foreground mb-1">Description</label>
                  <textarea
                    value={videoForm.description}
                    onChange={(e) => setVideoForm({ ...videoForm, description: e.target.value })}
                    className="w-full px-4 py-3 bg-background border border-border rounded-xl text-foreground"
                    rows={2}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-muted-foreground mb-1">Duration (seconds)</label>
                    <input
                      type="number"
                      value={videoForm.duration}
                      onChange={(e) => setVideoForm({ ...videoForm, duration: e.target.value })}
                      className="w-full px-4 py-3 bg-background border border-border rounded-xl text-foreground"
                      placeholder="e.g., 1800 for 30 min"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-muted-foreground mb-1">Order in Course</label>
                    <input
                      type="number"
                      value={videoForm.order}
                      onChange={(e) => setVideoForm({ ...videoForm, order: e.target.value })}
                      className="w-full px-4 py-3 bg-background border border-border rounded-xl text-foreground"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold text-muted-foreground mb-1">Assign to Course</label>
                  <select
                    value={videoForm.course_id}
                    onChange={(e) => setVideoForm({ ...videoForm, course_id: e.target.value })}
                    className="w-full px-4 py-3 bg-background border border-border rounded-xl text-foreground"
                  >
                    <option value="">No course (standalone)</option>
                    {courses.map(course => (
                      <option key={course.id} value={course.id}>{course.title}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-bold text-muted-foreground mb-1">Video URL (Vimeo) *</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={videoForm.video_url}
                      onChange={(e) => setVideoForm({ ...videoForm, video_url: e.target.value })}
                      className="flex-1 px-4 py-3 bg-background border border-border rounded-xl text-foreground"
                      placeholder="https://player.vimeo.com/video/123456789"
                    />
                    <button 
                      type="button"
                      onClick={() => openMediaPicker('video_url')}
                      className="px-4 py-3 bg-green-500/20 text-green-500 rounded-xl hover:bg-green-500/30 transition-colors flex items-center gap-2"
                    >
                      <Library className="w-5 h-5" />
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Paste Vimeo embed URL or browse library</p>
                </div>

                <div>
                  <label className="block text-sm font-bold text-muted-foreground mb-1">Thumbnail</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={videoForm.thumbnail_url}
                      onChange={(e) => setVideoForm({ ...videoForm, thumbnail_url: e.target.value })}
                      className="flex-1 px-4 py-3 bg-background border border-border rounded-xl text-foreground"
                    />
                    <button 
                      type="button"
                      onClick={() => openMediaPicker('video_thumbnail')}
                      className="px-4 py-3 bg-amber-500/20 text-amber-500 rounded-xl hover:bg-amber-500/30 transition-colors flex items-center gap-2"
                    >
                      <Library className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold text-muted-foreground mb-1">Trailer URL (30-60 sec preview)</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={videoForm.trailer_url}
                      onChange={(e) => setVideoForm({ ...videoForm, trailer_url: e.target.value })}
                      className="flex-1 px-4 py-3 bg-background border border-border rounded-xl text-foreground"
                    />
                    <button 
                      type="button"
                      onClick={() => openMediaPicker('video_trailer')}
                      className="px-4 py-3 bg-purple-500/20 text-purple-500 rounded-xl hover:bg-purple-500/30 transition-colors flex items-center gap-2"
                    >
                      <Library className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>

              <div className="p-4 border-t border-border flex gap-3 justify-end">
                <button
                  onClick={() => setShowVideoModal(false)}
                  className="px-6 py-3 bg-background border border-border rounded-xl font-medium text-foreground hover:bg-card transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveVideo}
                  disabled={uploading}
                  className="flex items-center gap-2 px-6 py-3 bg-primary hover:bg-primary/90 rounded-xl font-bold text-primary-foreground transition-colors disabled:opacity-50"
                >
                  <Save className="w-4 h-4" />
                  {uploading ? 'Uploading...' : (editingVideo ? 'Update' : 'Create')}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Media Library Picker */}
      {showMediaPicker && (
        <MediaLibraryPicker
          isOpen={showMediaPicker}
          onClose={() => {
            setShowMediaPicker(false);
            setMediaPickerTarget(null);
          }}
          onSelect={handleMediaSelect}
          multiple={false}
          filterType={mediaPickerTarget?.includes('thumbnail') ? 'image' : 'video'}
        />
      )}
    </div>
  );
};
